import React, { useState, useEffect, useRef } from 'react';
import Api from "@app/api/Api.js";
import CartProductVariationItem from "@app/react/components/CartProductVariationItem/CartProductVariationItem.jsx";
import './styles.scss';

const LIMIT = 20;
const DEBOUNCE_MS = 400;

// ─────────────────────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────────────────────
export default function ProductSearch({ colorGroups = [], collectionGroups = [] }) {
  const [params, setParams] = useState({
    name:            '',
    sku:             '',
    colorIds:        [],
    collectionValue: '',   // "categoryId_collectionId" oppure ''
    offset:          0,
  });

  const [items, setItems]               = useState([]);
  const [quantities, setQuantities]     = useState({});
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [cartMessage, setCartMessage]   = useState(null);

  // Fetch debounced — si ri-esegue ogni volta che params cambia.
  useEffect(() => {
    const timer = setTimeout(() => {
      const query = { offset: params.offset, limit: LIMIT };
      if (params.name.trim())         query.name     = params.name.trim();
      if (params.sku.trim())          query.sku      = params.sku.trim();
      if (params.colorIds.length > 0) query.colorIds = params.colorIds;

      if (params.collectionValue) {
        const [catId, collId] = params.collectionValue.split('_');
        query.categoryId   = parseInt(catId);
        query.collectionId = parseInt(collId);
      }

      setLoading(true);
      Api.get('api/products', query)
        .then(response => {
          setItems(response.items ?? []);
          setTotal(response.total ?? 0);
          setQuantities({});
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [params]);

  // Filtri — resettano sempre l'offset a 0.
  function handleFilterChange(field, value) {
    setParams(prev => ({ ...prev, [field]: value, offset: 0 }));
  }

  // Paginazione — cambia solo offset.
  function handlePageChange(newOffset) {
    setParams(prev => ({ ...prev, offset: newOffset }));
  }

  function handleQuantityChange(variationId, qty) {
    setQuantities(prev => ({ ...prev, [variationId]: qty }));
  }

  function handleAddToCart() {
    const cartItems = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ product_variation_id: parseInt(id), quantity: qty }));

    if (cartItems.length === 0) return;

    Api.post('api/cart', { items: cartItems })
      .then(() => {
        showCartMessage('success', Drupal.tt('Prodotti aggiunti al carrello'));
        setQuantities({});
      })
      .catch(() => {
        showCartMessage('error', Drupal.tt("Errore durante l'aggiunta al carrello"));
      });
  }

  function showCartMessage(type, text) {
    setCartMessage({ type, text });
    setTimeout(() => setCartMessage(null), 4000);
  }

  const selectedCount = Object.values(quantities).filter(q => q > 0).length;
  const totalPages    = Math.ceil(total / LIMIT);
  const currentPage   = Math.floor(params.offset / LIMIT) + 1;

  return (
    <div className="product-search">

      {/* ── Filtri ── */}
      <div className="product-search__filters">
        <div className="product-search__filter">
          <label>{Drupal.tt('Nome prodotto')}</label>
          <input
            type="text"
            value={params.name}
            onChange={e => handleFilterChange('name', e.target.value)}
            placeholder={Drupal.tt('Cerca per nome…')}
          />
        </div>

        <div className="product-search__filter">
          <label>{Drupal.tt('SKU')}</label>
          <input
            type="text"
            value={params.sku}
            onChange={e => handleFilterChange('sku', e.target.value)}
            placeholder={Drupal.tt('Cerca per codice…')}
          />
        </div>

        <div className="product-search__filter product-search__filter--collection">
          <label>{Drupal.tt('Categoria / Collezione')}</label>
          <select
            value={params.collectionValue}
            onChange={e => handleFilterChange('collectionValue', e.target.value)}
            className="product-search__select"
          >
            <option value="">{Drupal.tt('— Tutte le collezioni —')}</option>
            {collectionGroups.map(group => (
              <optgroup key={group.id} label={group.name}>
                {group.collections.map(coll => (
                  <option key={coll.value} value={coll.value}>
                    {coll.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="product-search__filter product-search__filter--color">
          <label>{Drupal.tt('Colore')}</label>
          <ColorSelector
            colorGroups={colorGroups}
            selectedIds={params.colorIds}
            onChange={ids => handleFilterChange('colorIds', ids)}
          />
        </div>
      </div>

      {/* ── Sommario risultati ── */}
      <div className="product-search__summary">
        {!loading && total > 0 && (
          <span>
            {Drupal.ttPlural(total, '1 prodotto trovato', '@count prodotti trovati')}
          </span>
        )}
      </div>

      {/* ── Risultati ── */}
      <div className="product-search__results">
        {loading && (
          <div className="product-search__state">{Drupal.tt('Ricerca in corso…')}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="product-search__state">{Drupal.tt('Nessun prodotto trovato')}</div>
        )}
        {!loading && items.map(item => (
          <CartProductVariationItem
            key={item.id}
            productVariation={item}
            quantity={quantities[item.id] ?? 0}
            onChange={qty => handleQuantityChange(item.id, qty)}
          />
        ))}
      </div>

      {/* ── Paginazione ── */}
      {!loading && totalPages > 1 && (
        <div className="product-search__pagination">
          <button
            className="btn btn-secondary"
            disabled={params.offset === 0}
            onClick={() => handlePageChange(Math.max(0, params.offset - LIMIT))}
          >
            {Drupal.tt('Precedente')}
          </button>
          <span className="product-search__pagination-info">
            {currentPage} / {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={params.offset + LIMIT >= total}
            onClick={() => handlePageChange(params.offset + LIMIT)}
          >
            {Drupal.tt('Successivo')}
          </button>
        </div>
      )}

      {/* ── Barra aggiungi al carrello ── */}
      {selectedCount > 0 && (
        <div className="product-search__cart-bar">
          <span className="product-search__cart-bar-label">
            {Drupal.ttPlural(selectedCount, '1 prodotto selezionato', '@count prodotti selezionati')}
          </span>
          {cartMessage && (
            <span className={`product-search__cart-message product-search__cart-message--${cartMessage.type}`}>
              {cartMessage.text}
            </span>
          )}
          <button className="btn btn-primary" onClick={handleAddToCart}>
            {Drupal.tt('Aggiungi al carrello')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ColorSelector — multi-selezione raggruppata per materiale
// ─────────────────────────────────────────────────────────────────────────────
function ColorSelector({ colorGroups, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef    = useRef(null);

  // Chiude il dropdown se si clicca fuori
  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  function toggleColor(id) {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(i => i !== id)
        : [...selectedIds, id]
    );
  }

  const count = selectedIds.length;

  return (
    <div
      className={`color-selector${open ? ' color-selector--open' : ''}${count > 0 ? ' color-selector--active' : ''}`}
      ref={containerRef}
    >
      <button
        type="button"
        className="color-selector__trigger"
        onClick={() => setOpen(p => !p)}
      >
        <span>{Drupal.tt('Seleziona colore')}</span>
        {count > 0 && <span className="color-selector__badge">{count}</span>}
        <span className="color-selector__arrow">▾</span>
      </button>

      {open && (
        <div className="color-selector__dropdown">
          {count > 0 && (
            <button type="button" className="color-selector__clear" onClick={() => onChange([])}>
              {Drupal.tt('Deseleziona tutti')}
            </button>
          )}
          {colorGroups.map(group => (
            <div key={group.id} className="color-selector__group">
              <div className="color-selector__group-label">{group.name}</div>
              <div className="color-selector__group-items">
                {group.colors.map(color => {
                  const selected = selectedIds.includes(color.id);
                  return (
                    <div
                      key={color.id}
                      className={`color-selector__item${selected ? ' color-selector__item--selected' : ''}`}
                      onClick={() => toggleColor(color.id)}
                    >
                      <ColorIcon color={color} />
                      <span className="color-selector__item-name">{color.name}</span>
                      {selected && <span className="color-selector__item-check">✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {colorGroups.length === 0 && (
            <div className="color-selector__empty">{Drupal.tt('Nessun colore disponibile')}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Icona colore 10×10 px
function ColorIcon({ color }) {
  if (color.image) {
    return <img src={color.image} alt="" className="color-selector__icon" />;
  }
  if (color.code) {
    return <span className="color-selector__icon color-selector__icon--swatch" style={{ background: color.code }} />;
  }
  return <span className="color-selector__icon color-selector__icon--empty" />;
}
